import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemBanco } from '../mensagemBanco';
import {
  FILTROS_INICIAIS,
  deCandidato,
  deMontagem,
  filtra,
  ordenarEquipe,
} from '../montagem';
import type { EquipePlacar, FiltrosMontagem, PessoaMontagem } from '../types';
import BarraFiltrosMontagem from './BarraFiltrosMontagem';
import BlocoSobreEquipe from './BlocoSobreEquipe';
import CartaoPessoa from './CartaoPessoa';
import ModalConfirmarAcao from './ModalConfirmarAcao';
import PainelFicha from './PainelFicha';
import type { Selecao } from './ColunaEquipes';

interface Props {
  eventoId: number;
  selecionado: Selecao;
  equipes: EquipePlacar[];
  aoToast: (msg: string) => void;
  aoQuadroMudou: () => void;
}

interface Confirmacao {
  titulo: string;
  mensagem: string;
  acao: () => Promise<void>;
}

const PAGE = 40;

export default function ColunaMontagem({
  eventoId,
  selecionado,
  equipes,
  aoToast,
  aoQuadroMudou,
}: Props) {
  const equipeId = typeof selecionado === 'number' ? selecionado : null;

  const [filtros, setFiltros] = useState<FiltrosMontagem>(FILTROS_INICIAIS);
  const [pessoas, setPessoas] = useState<PessoaMontagem[]>([]);
  const [todos, setTodos] = useState<PessoaMontagem[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [carregandoMais, setCarregandoMais] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [temMais, setTemMais] = useState(false);
  const [confirmacao, setConfirmacao] = useState<Confirmacao | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [fotos, setFotos] = useState<Record<number, string>>({});
  const [fichaAbertaId, setFichaAbertaId] = useState<number | null>(null);

  const reqId = useRef(0);
  const primeiraRender = useRef(true);
  // Caminhos das fotos de rosto (inscricao_id -> caminho no bucket 'fichas')
  // e quando os links assinados expiram. Ficam só na memória da tela.
  const caminhosRef = useRef<Record<number, string>>({});
  const expiraEmRef = useRef<number | null>(null);

  // No modo equipe, `todos` guarda o resultado BRUTO de candidatos_para_equipe
  // e a lista visível é derivada localmente — sem nova RPC a cada tecla.
  const filtrados = useMemo(
    () => (equipeId === null ? [] : todos.filter((p) => filtra(p, filtros))),
    [equipeId, todos, filtros],
  );

  // Pessoa com a ficha aberta, sempre lida da lista viva (reflete alocação).
  const fontePessoas = equipeId !== null ? todos : pessoas;
  const pessoaFicha =
    fichaAbertaId !== null
      ? (fontePessoas.find((p) => p.inscricao_id === fichaAbertaId) ?? null)
      : null;

  async function buscarServidor(from: number, to: number) {
    const tabela = selecionado === 'sem-equipe' ? 'vw_sem_equipe' : 'vw_montagem';
    const f = filtros;
    // Só a participação Equipe entra na montagem das equipes de serviço.
    // vw_sem_equipe não tem a coluna na_montagem (já traz só Equipe).
    const base = supabase
      .from(tabela)
      .select('*', { count: 'exact' })
      .eq('evento_id', eventoId);
    let q = tabela === 'vw_montagem' ? base.eq('na_montagem', true) : base;

    if (f.busca.trim()) q = q.ilike('nome', `%${f.busca.trim()}%`);
    if (f.talentos.trim()) q = q.ilike('talentos', `%${f.talentos.trim()}%`);
    if (f.soSemEquipe) q = q.is('equipe_id', null);
    if (f.primeiroServir) q = q.eq('primeiro_servir', true);
    if (f.jaServiu) q = q.gt('quantas_equipes_ja_serviu', 0);
    if (f.fica5Dias) q = q.eq('todos_os_dias', true);
    if (f.vaiPastoreio) q = q.eq('vai_ao_pastoreio', true);
    if (f.sexo) q = q.eq('sexo', f.sexo);
    if (f.cidade.trim()) q = q.ilike('cidade', `%${f.cidade.trim()}%`);
    if (f.paroquia.trim()) q = q.ilike('paroquia', `%${f.paroquia.trim()}%`);
    if (f.temFilhos) q = q.eq('tem_filhos', true);
    if (f.temRestricao) q = q.eq('tem_restricao', true);

    const { data, count, error } = await q.order('nome').range(from, to);
    if (error) throw error;
    return { linhas: (data as Record<string, unknown>[]).map(deMontagem), total: count ?? 0 };
  }

  async function carregar() {
    const id = ++reqId.current;
    setCarregando(true);
    setErro(null);
    try {
      if (equipeId !== null) {
        const { data, error } = await supabase.rpc('candidatos_para_equipe', {
          p_equipe: equipeId,
        });
        if (error) throw error;
        if (reqId.current !== id) return;
        setTodos(
          ordenarEquipe((data as Record<string, unknown>[]).map(deCandidato), equipeId),
        );
      } else {
        const alvo = Math.max(PAGE - 1, pessoas.length - 1);
        const { linhas, total: n } = await buscarServidor(0, alvo);
        if (reqId.current !== id) return;
        setTodos([]);
        setPessoas(linhas);
        setTotal(n);
        setTemMais(n > linhas.length);
      }
    } catch (e) {
      if (reqId.current === id) setErro(mensagemBanco(e));
    } finally {
      if (reqId.current === id) setCarregando(false);
    }
  }

  // Recarrega de imediato ao trocar a seleção (equipe / sem equipe / todos).
  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selecionado, eventoId]);

  // No modo equipe, a lista visível acompanha o filtro localmente.
  useEffect(() => {
    if (equipeId === null) return;
    setPessoas(filtrados.slice(0, PAGE));
    setTotal(filtrados.length);
    setTemMais(filtrados.length > PAGE);
  }, [equipeId, filtrados]);

  // Recarrega com debounce ao mudar um filtro (ignora a primeira montagem).
  // No modo equipe o filtro é 100% local (via `filtrados`), então nada a fazer.
  useEffect(() => {
    if (primeiraRender.current) {
      primeiraRender.current = false;
      return;
    }
    if (equipeId !== null) return;
    const t = setTimeout(carregar, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros]);

  // Assina TODOS os caminhos de uma vez (uma chamada só para os 315).
  // Os links vencem em 1 hora; os caminhos continuam guardados na memória
  // para re-assinar quando vencer, sem recarregar a tela.
  const assinarFotos = useCallback(async (caminhoPorInscricao: Record<number, string>) => {
    const caminhos = Array.from(new Set(Object.values(caminhoPorInscricao)));
    if (caminhos.length === 0) return;
    const { data, error } = await supabase.storage
      .from('fichas')
      .createSignedUrls(caminhos, 3600);
    if (error) return;
    const porCaminho: Record<string, string> = {};
    (data ?? []).forEach((item) => {
      if (item?.signedUrl && item.path) porCaminho[item.path] = item.signedUrl;
    });
    const novo: Record<number, string> = {};
    Object.entries(caminhoPorInscricao).forEach(([id, caminho]) => {
      const url = porCaminho[caminho];
      if (url) novo[Number(id)] = url;
    });
    setFotos(novo);
    expiraEmRef.current = Date.now() + 3600 * 1000;
  }, []);

  // Lê as fotos da FICHA DE INSCRIÇÃO direto da view de respostas
  // (mesmo caminho comprovado da aba de Inscritos). Pega apenas respostas de
  // tipo 'foto' — a foto do cadastro (conta Google) nunca entra aqui.
  async function carregarFotosDosInscritos() {
    try {
      const { data: inscData, error: errInsc } = await supabase
        .from('inscricoes')
        .select('id')
        .eq('evento_id', eventoId);
      if (errInsc) return;
      const ids = ((inscData as { id: number }[] | null) ?? []).map((i) => i.id);
      if (ids.length === 0) return;

      const { data: respData, error: errResp } = await supabase
        .from('vw_respostas_inscricao')
        .select('inscricao_id, resposta_texto')
        .eq('tipo', 'foto')
        .in('inscricao_id', ids);
      if (errResp) return;

      const linhas =
        (respData as { inscricao_id: number; resposta_texto: string | null }[] | null) ?? [];
      const caminhoPorInscricao: Record<number, string> = {};
      linhas.forEach((l) => {
        if (l.resposta_texto && !caminhoPorInscricao[l.inscricao_id]) {
          caminhoPorInscricao[l.inscricao_id] = l.resposta_texto;
        }
      });
      caminhosRef.current = caminhoPorInscricao;
      await assinarFotos(caminhoPorInscricao);
    } catch {
      // Fotos são opcionais: se falhar, o cartão mostra as iniciais.
    }
  }

  useEffect(() => {
    carregarFotosDosInscritos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId]);

  // Perto do vencimento (1 h), pede links novos sozinho, sem quebrar a tela.
  useEffect(() => {
    const t = setInterval(() => {
      if (expiraEmRef.current && Date.now() > expiraEmRef.current - 5 * 60 * 1000) {
        assinarFotos(caminhosRef.current);
      }
    }, 60 * 1000);
    return () => clearInterval(t);
  }, [assinarFotos]);

  async function carregarMais() {
    if (carregandoMais) return;
    setCarregandoMais(true);
    try {
      if (equipeId !== null) {
        const novo = Math.min(pessoas.length + PAGE, filtrados.length);
        setPessoas(filtrados.slice(0, novo));
        setTemMais(novo < filtrados.length);
      } else {
        const from = pessoas.length;
        const { linhas, total: n } = await buscarServidor(from, from + PAGE - 1);
        const juntos = [...pessoas, ...linhas];
        setPessoas(juntos);
        setTemMais(n > juntos.length);
      }
    } catch (e) {
      aoToast(mensagemBanco(e));
    } finally {
      setCarregandoMais(false);
    }
  }

  // Atualiza a linha localmente sem recarregar tudo. No modo equipe, mexe no
  // `todos` (que alimenta o filtro); nos demais modos, mexe direto em `pessoas`.
  function patchLocal(inscricaoId: number, patch: Partial<PessoaMontagem>) {
    if (equipeId !== null) {
      setTodos((prev) =>
        ordenarEquipe(
          prev.map((x) => (x.inscricao_id === inscricaoId ? { ...x, ...patch } : x)),
          equipeId,
        ),
      );
      return;
    }
    setPessoas((prev) => {
      const atualizada = prev.map((x) =>
        x.inscricao_id === inscricaoId ? { ...x, ...patch } : x,
      );
      return selecionado === 'sem-equipe'
        ? atualizada.filter((x) => x.equipe_id === null)
        : atualizada;
    });
  }

  async function executarAlocar(p: PessoaMontagem, alvoId: number) {
    try {
      const { error } = await supabase.rpc('alocar', {
        p_inscricao: p.inscricao_id,
        p_equipe: alvoId,
      });
      if (error) throw error;

      const nomeAlvo = equipes.find((e) => e.equipe_id === alvoId)?.equipe ?? '';
      patchLocal(p.inscricao_id, {
        equipe_id: alvoId,
        equipe_atual: nomeAlvo,
        status_alocacao: null,
      });
      aoQuadroMudou();
    } catch (e) {
      aoToast(mensagemBanco(e));
    }
  }

  async function aoDesalocar(p: PessoaMontagem) {
    try {
      const { error } = await supabase.rpc('desalocar', { p_inscricao: p.inscricao_id });
      if (error) throw error;
      patchLocal(p.inscricao_id, {
        equipe_id: null,
        equipe_atual: null,
        status_alocacao: null,
      });
      aoQuadroMudou();
    } catch (e) {
      aoToast(mensagemBanco(e));
    }
  }

  function aoAlocar(p: PessoaMontagem) {
    if (equipeId === null) return;
    const alvo = equipes.find((e) => e.equipe_id === equipeId);
    const nomeAlvo = alvo?.equipe ?? '';
    if (p.recusou) {
      setConfirmacao({
        titulo: 'Colocar mesmo assim?',
        mensagem: 'Essa pessoa pediu para não servir aqui.',
        acao: () => executarAlocar(p, equipeId),
      });
      return;
    }
    if (p.equipe_atual) {
      setConfirmacao({
        titulo: 'Mover pessoa',
        mensagem: `${p.nome} está em ${p.equipe_atual}. Mover para ${nomeAlvo}?`,
        acao: () => executarAlocar(p, equipeId),
      });
      return;
    }
    executarAlocar(p, equipeId);
  }

  function aoEscolherEquipe(p: PessoaMontagem, alvoId: number) {
    const alvo = equipes.find((e) => e.equipe_id === alvoId);
    const nomeAlvo = alvo?.equipe ?? '';
    if (p.equipe_atual) {
      setConfirmacao({
        titulo: 'Mover pessoa',
        mensagem: `${p.nome} está em ${p.equipe_atual}. Mover para ${nomeAlvo}?`,
        acao: () => executarAlocar(p, alvoId),
      });
      return;
    }
    executarAlocar(p, alvoId);
  }

  async function executarConfirmarEquipe() {
    if (equipeId === null) return;
    try {
      const { error } = await supabase.rpc('confirmar_equipe', { p_equipe: equipeId });
      if (error) throw error;
      setTodos((prev) =>
        prev.map((x) => (x.equipe_id === equipeId ? { ...x, status_alocacao: 'confirmada' } : x)),
      );
      aoQuadroMudou();
    } catch (e) {
      aoToast(mensagemBanco(e));
    }
  }

  function aoConfirmarEquipe() {
    const alvo = equipes.find((e) => e.equipe_id === equipeId);
    setConfirmacao({
      titulo: 'Confirmar equipe',
      mensagem: `Confirmar a equipe ${alvo?.equipe ?? ''}? Os outros diretores verão as pessoas como confirmadas.`,
      acao: executarConfirmarEquipe,
    });
  }

  async function confirmarExecucao() {
    if (!confirmacao) return;
    setSalvando(true);
    try {
      await confirmacao.acao();
    } finally {
      setSalvando(false);
      setConfirmacao(null);
    }
  }

  const titulo =
    equipeId !== null
      ? (equipes.find((e) => e.equipe_id === equipeId)?.equipe ?? '')
      : selecionado === 'sem-equipe'
        ? 'Sem equipe'
        : 'Todos os inscritos';

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="truncate text-base font-semibold text-foreground-950">{titulo}</h2>
          <p className="text-sm text-secondary-500">{total} pessoas</p>
        </div>

        {equipeId !== null && (
          <button
            type="button"
            onClick={aoConfirmarEquipe}
            className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-white px-3 py-2 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-check-double-line" />
            </span>
            Confirmar esta equipe
          </button>
        )}
      </div>

      {equipeId !== null && (
        <BlocoSobreEquipe
          key={equipeId}
          equipeId={equipeId}
          nome={titulo}
          aoToast={aoToast}
          aoMudou={() => {
            carregar();
            aoQuadroMudou();
          }}
        />
      )}

      <BarraFiltrosMontagem filtros={filtros} aoMudar={setFiltros} />

      <div className="mt-4">
        {erro ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-8 text-center">
            <p className="text-sm text-red-700">{erro}</p>
            <button
              type="button"
              onClick={carregar}
              className="mt-3 cursor-pointer whitespace-nowrap rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-100"
            >
              Tentar de novo
            </button>
          </div>
        ) : carregando && pessoas.length === 0 ? (
          <div className="space-y-3">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-24 animate-pulse rounded-lg bg-secondary-100" />
            ))}
          </div>
        ) : pessoas.length === 0 ? (
          <div className="rounded-lg border border-secondary-200 bg-white px-6 py-12 text-center">
            <p className="text-sm text-secondary-500">Ninguém encontrado com esses filtros.</p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {pessoas.map((p) => (
                <CartaoPessoa
                  key={p.inscricao_id}
                  pessoa={p}
                  equipeId={equipeId}
                  equipes={equipes}
                  fotoUrl={fotos[p.inscricao_id] ?? null}
                  aoAlocar={aoAlocar}
                  aoDesalocar={aoDesalocar}
                  aoEscolherEquipe={aoEscolherEquipe}
                  aoAbrirFicha={() => setFichaAbertaId(p.inscricao_id)}
                />
              ))}
            </div>

            {temMais && (
              <div className="mt-4 flex justify-center">
                <button
                  type="button"
                  onClick={carregarMais}
                  disabled={carregandoMais}
                  className="cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-5 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {carregandoMais ? 'Carregando…' : 'Carregar mais'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {confirmacao && (
        <ModalConfirmarAcao
          titulo={confirmacao.titulo}
          mensagem={confirmacao.mensagem}
          textoConfirmar="Confirmar"
          salvando={salvando}
          aoConfirmar={confirmarExecucao}
          aoCancelar={() => setConfirmacao(null)}
        />
      )}

      {pessoaFicha && (
        <PainelFicha
          pessoa={pessoaFicha}
          fotoUrl={fotos[pessoaFicha.inscricao_id] ?? null}
          equipes={equipes}
          aoFechar={() => setFichaAbertaId(null)}
          aoEscolherEquipe={aoEscolherEquipe}
          aoDesalocar={aoDesalocar}
          aoToast={aoToast}
        />
      )}
    </div>
  );
}